import { useEffect, useState, useCallback } from "react";
import { catalog, ContentType } from "../api/tmdb";
import PosterRow from "../components/PosterRow";
import PosterCard from "../components/PosterCard";
import { LoadingScreen, ErrorScreen, EmptyScreen } from "../components/StatusScreens";
import { getProgress } from "../storage/progress";

// Genre chips on the Movies page — tapping one swaps the rows for a full
// browse grid of that genre (TMDB genre ids).
const GENRES = [
  { id: "28", label: "Action" },
  { id: "35", label: "Comedy" },
  { id: "27", label: "Horror" },
  { id: "10749", label: "Romance" },
  { id: "16", label: "Animation" },
  { id: "53", label: "Thriller" },
  { id: "878", label: "Sci-Fi" },
  { id: "18", label: "Drama" },
  { id: "12", label: "Adventure" },
  { id: "14", label: "Fantasy" },
  { id: "80", label: "Crime" },
  { id: "9648", label: "Mystery" },
];

export default function CatalogScreen({ kind }) {
  const isMovies = kind === "movies";
  const [state, setState] = useState({ loading: true, rows: [], error: null });
  const [genre, setGenre] = useState(null);
  const [genreState, setGenreState] = useState({ loading: false, items: [] });

  const load = useCallback(() => {
    setState({ loading: true, rows: [], error: null });
    (isMovies ? catalog.movieRows() : catalog.tvRows())
      .then((rows) => setState({ loading: false, rows }))
      .catch((error) => setState({ loading: false, rows: [], error: error.message || "Something went wrong. Try again." }));
  }, [isMovies]);

  useEffect(load, [load]);

  const openGenre = useCallback((g) => {
    setGenre(g);
    if (g == null) {
      setGenreState({ loading: false, items: [] });
      return;
    }
    setGenreState({ loading: true, items: [] });
    catalog
      .genreMovies(g)
      .then((items) => setGenreState({ loading: false, items }))
      .catch(() => setGenreState({ loading: false, items: [] }));
  }, []);

  if (state.loading) return <LoadingScreen label="Loading…" />;
  if (state.error) return <ErrorScreen message={state.error} onRetry={load} />;
  if (state.rows.length === 0) return <ErrorScreen message="Nothing to show right now." onRetry={load} />;

  const getCardProgress = (card) =>
    card.contentType === ContentType.EPISODE ? null : getProgress(card.contentType, card.tmdbId);

  return (
    <main className="page catalog-page">
      <div className="page-content">
        <h1 className="page-heading">{isMovies ? "Movies" : "TV Shows"}</h1>
        {isMovies && (
          <div className="language-chips" role="group" aria-label="Browse by genre">
            <button className={`chip${genre == null ? " active" : ""}`} onClick={() => openGenre(null)}>
              All
            </button>
            {GENRES.map((g) => (
              <button key={g.id} className={`chip${genre === g.id ? " active" : ""}`} onClick={() => openGenre(g.id)}>
                {g.label}
              </button>
            ))}
          </div>
        )}
        {isMovies && genre != null ? (
          genreState.loading ? (
            <LoadingScreen label="Loading genre…" />
          ) : genreState.items.length === 0 ? (
            <EmptyScreen title="Nothing here yet" message="No movies found for this genre right now." />
          ) : (
            <div className="results-grid">
              {genreState.items.map((card) => (
                <PosterCard key={`mv-${card.tmdbId}`} card={card} progress={getCardProgress(card)} />
              ))}
            </div>
          )
        ) : (
          state.rows.map((row) => (
            <PosterRow key={row.id} title={row.title} items={row.items} getProgress={getCardProgress} wide={!isMovies} />
          ))
        )}
      </div>
    </main>
  );
}